/**
 * The zone flyout — the affirmation questions, asked ON the flesh (ADR-031
 * §9.3, D8).
 *
 * Allen, ruling D8: *"that UX is magic to use… I'd even consider making the
 * knobs happen on flyout menus on click if it's immediately relevant."* So the
 * zone's knobs live here, anchored to the zone's own flesh, exactly as the
 * digit bar is anchored to its disc — the same DOM-over-WebGPU reasoning
 * (crisp text, keyboard path, follows the character every frame).
 *
 * WHAT IT ASKS, for T1: what this flesh IS (tail / cloth — the tube-or-sheet
 * override, a human act), how far the zone reaches (wider / tighter — the
 * detector's threshold, re-measured), and whether it should exist at all (✕).
 * T2 (ADR-031 §3.3a) adds the chain: on flesh a person called a tail, "grow
 * chain" places one (the count defaults from length over radius), then
 * "more / fewer bones" re-places it on the spot (D4 — count is curvature, not
 * stiffness) and "remove chain" withdraws it. A refusal is RENDERED on the bar
 * — the kernel's own sentence, naming the zone's kind and the band — because a
 * refusal that only reaches the console never reached the person.
 *
 * A pure view: renders what it is handed, reports trusted clicks, follows the
 * anchor. What a click DOES is main's business. Depends on: nothing.
 * Depended on by: main.
 */

export type ZoneIntentChoice = 'tail' | 'cloth' | 'softTissue';
export type TailAction = 'grow' | 'grow-centred' | 'group' | 'group-centred' | 'centre' | 'more' | 'fewer' | 'reset-root' | 'withdraw';

/** The chain on this zone, as the run placed it. */
export interface ZoneTailState {
  readonly bones: number;
  readonly provenance: 'default' | 'declared';
  /** What the default rule would choose — shown beside a declared count. */
  readonly defaultCount: number | null;
  readonly lengthCm: number | null;
  /** A person placed Tail1's pivot (the root handle); "root: reset" is offered. */
  readonly pivotPlaced: boolean;
}

export interface ZoneFlyoutDeps {
  /** Where the zone's flesh is on screen right now, or null if it is not drawn. */
  screenXY(): { readonly x: number; readonly y: number } | null;
  /** "This is a tail" / "this is cloth". `trusted` is the click's `isTrusted` — a forged click confirms nothing. */
  onIntent(intent: ZoneIntentChoice, trusted: boolean): void;
  /** Re-measure with the reach moved one RING toward (+1) or away from (−1) the body. */
  onReach(direction: 1 | -1): void;
  /** EXTEND TIPS on a fan: take (or give back) the fan's other tails beyond the seam. */
  onTips(): void;
  /** THE WAND: arm ➕ / ➖ (the same mode again disarms). While armed, a click on the model is a stroke. */
  onWand(mode: 'add' | 'remove', trusted: boolean): void;
  /** T2: grow / re-count / withdraw the chain. `trusted` is the click's `isTrusted`. */
  onTail(action: TailAction, trusted: boolean): void;
  /** The bar closed, by any route — Esc, done, an outside click, its flesh leaving the screen. */
  onClosed(): void;
  /** Throw the zone away. */
  onDelete(): void;
  onDismiss(): void;
}

export interface ZoneFlyoutState {
  readonly name: string;
  readonly faces: number;
  /** The detector's reading — shown as evidence beside the question. */
  readonly shape: 'tube' | 'sheet' | 'blob' | 'stub' | null;
  readonly intent: ZoneIntentChoice | null;
  /** How many rings toward the body the face set was grown by (negative: shed at the body), or null if hand-edited. */
  readonly rings: number | null;
  /**
   * A detected tail steps its reach by DIRECTION (Allen, 2026-09-25, Kitty):
   * wider grows only toward the body ("extend stem"). Other zones keep "wider".
   */
  readonly reachKind: 'tail' | 'region';
  /** A FAN's extend-tips state: true = the fan's other tails are taken; null = not a fan. */
  readonly tips: boolean | null;
  /** The wand's armed mode, or null. */
  readonly wand: 'add' | 'remove' | null;
  /** The bones that own this flesh today, e.g. "R_ThighTwist01 53%". */
  readonly ownedBy: string;
  /** T2: the chain, or null when none is declared. */
  readonly tail: ZoneTailState | null;
  /** T2: a refusal or a progress line to show on the bar, or null. */
  readonly tailNote: string | null;
  /** A FAN of tails shares this zone's root: why no chain is offered (the kitsune), or null. */
  readonly fanRefusal: string | null;
  /** How far off centre a detected single tail sits — evidence on the label's tooltip — or null. */
  readonly centring: string | null;
  /**
   * CENTRE THE TAIL is offered (a single tail swept this many degrees to one
   * side, not yet centred), or null. Allen, 2026-09-24: a single tail's
   * baseline is centre-back.
   */
  readonly centreOffer: { readonly degrees: number; readonly side: string } | null;
}

export interface ZoneFlyout {
  open(state: ZoneFlyoutState): void;
  update(state: ZoneFlyoutState): void;
  close(): void;
  readonly isOpen: boolean;
  dispose(): void;
}

export function createZoneFlyout(host: HTMLElement, deps: ZoneFlyoutDeps): ZoneFlyout {
  const root = document.createElement('div');
  // Its OWN class: the smoke finds the digit bar by `.digit-flyout`, and a
  // second element wearing it broke the locator (CI, #136). The glass styling
  // is shared by selector in index.html, not by class.
  root.className = 'zone-flyout';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', 'this zone');
  root.hidden = true;
  host.append(root);

  let open = false;
  let frame = 0;
  let state: ZoneFlyoutState = {
    name: '',
    faces: 0,
    shape: null,
    intent: null,
    rings: null,
    reachKind: 'region',
    tips: null,
    wand: null,
    ownedBy: '',
    tail: null,
    tailNote: null,
    fanRefusal: null,
    centring: null,
    centreOffer: null,
  };

  // WHERE THE BAR SITS. The digit bar centres itself above its disc, which is
  // right for a fingertip and wrong for a zone: the anchor is a face in the
  // middle of the tail, so a centred bar covers the thing being judged (Allen,
  // read #3: "it's managing to block the critical work view — and I don't
  // have a way to move it"). Default: BESIDE the flesh — to the right of the
  // anchor with a gap, flipped left near the edge — and DRAGGABLE by its
  // label; the offset a person chooses is kept for the rest of the session.
  const SIDE_GAP = 36;
  let userOffset: { x: number; y: number } | null = null;
  let dragging: { startX: number; startY: number; fromX: number; fromY: number; pointerId: number } | null = null;

  function reposition(): void {
    const at = deps.screenXY();
    if (at === null) {
      close(); // a bar attached to nothing is a lie about which zone is being asked about
      return;
    }
    const rect = root.getBoundingClientRect();
    let x: number;
    let y: number;
    if (userOffset !== null) {
      x = at.x + userOffset.x;
      y = at.y + userOffset.y;
    } else {
      x = at.x + SIDE_GAP;
      if (x + rect.width > window.innerWidth - 8) x = at.x - SIDE_GAP - rect.width; // flip left near the edge
      y = at.y - rect.height / 2;
    }
    x = Math.min(Math.max(x, 8), window.innerWidth - rect.width - 8);
    y = Math.min(Math.max(y, 8), window.innerHeight - rect.height - 8);
    root.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    if (open) frame = requestAnimationFrame(reposition);
  }

  /** Drag by the label: the bar follows the pointer, and the offset from the anchor is remembered. */
  const onLabelDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    const at = deps.screenXY();
    if (at === null) return;
    const rect = root.getBoundingClientRect();
    dragging = { startX: event.clientX, startY: event.clientY, fromX: rect.left - at.x, fromY: rect.top - at.y, pointerId: event.pointerId };
    root.dataset['dragging'] = '';
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
  };
  const onLabelMove = (event: PointerEvent): void => {
    if (dragging === null || event.pointerId !== dragging.pointerId) return;
    userOffset = { x: dragging.fromX + (event.clientX - dragging.startX), y: dragging.fromY + (event.clientY - dragging.startY) };
  };
  const onLabelUp = (event: PointerEvent): void => {
    if (dragging === null || event.pointerId !== dragging.pointerId) return;
    dragging = null;
    delete root.dataset['dragging'];
  };

  const button = (label: string, title: string, onClick: (event: MouseEvent) => void, extra?: Record<string, string>): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.title = title;
    for (const [k, v] of Object.entries(extra ?? {})) b.dataset[k] = v;
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick(e);
    });
    return b;
  };

  // The rarely-used controls live behind "more…" (Allen's second field read:
  // "way too long … not everything has to reach the flag"). Remembered while
  // the bar is open, closed again when it closes.
  let showMore = false;

  const row = (name: string): HTMLDivElement => {
    const r = document.createElement('div');
    r.className = 'zone-flyout-row';
    r.dataset['row'] = name;
    root.append(r);
    return r;
  };

  /**
   * THREE SHORT ROWS, never one long one:
   *   1 · what this is — the name, the shape, and the answer (tail / cloth);
   *   2 · what the answer asks next — grow the bones / the count and fewer ·
   *       more, or cloth's plain "not yet";
   *   3 · done, and "more…", which opens a fourth row for the rest (wider,
 *       tighter, remove chain, remove zone) — closed again with the bar.
   */
  function render(): void {
    root.replaceChildren();
    const identity = row('identity');
    const label = document.createElement('span');
    label.className = 'flyout-knuckle-label zone-flyout-grip';
    label.textContent = `⋮⋮ ${state.name}${state.shape === null ? '' : ` · ${state.shape}`}`;
    // Evidence rides the label's tooltip — the bar stays three short rows
    // (Allen's second read: "way too long"): how far off centre a single
    // detected tail sits (the wolf's sideways sweep).
    label.title =
      `${state.faces} faces · drag to move the bar${state.ownedBy === '' ? '' : ` · owned today by ${state.ownedBy}`}` +
      (state.centring === null ? '' : ` · ${state.centring}`);
    if (state.centring !== null) label.dataset['tailCentring'] = state.centring;
    label.addEventListener('pointerdown', onLabelDown);
    label.addEventListener('pointermove', onLabelMove);
    label.addEventListener('pointerup', onLabelUp);
    label.addEventListener('pointercancel', onLabelUp);
    identity.append(label);
    for (const intent of ['tail', 'cloth', 'softTissue'] as const) {
      const current = state.intent === intent;
      const b = button(
        `${intent === 'softTissue' ? 'soft tissue' : intent}${current ? ' ✓' : ''}`,
        intent === 'tail'
          ? 'this flesh is a tail — bones can grow down it'
          : intent === 'cloth'
            ? 'this flesh is cloth — a sheet, not a chain'
            : 'this flesh is a breast, the belly or a glute — a bounce leaf can own it (Body → Soft tissue)',
        (e) => deps.onIntent(intent, e.isTrusted),
        { intent },
      );
      b.className = 'flyout-digit';
      b.setAttribute('aria-pressed', String(current));
      identity.append(b);
    }

    // Row 2 — the next step the answer asks for (D8: at the object, only when relevant).
    if (state.intent === 'tail') {
      const chain = row('chain');
      if (state.tail === null && state.fanRefusal !== null) {
        // A fan: no Grow button promising what one chain cannot do — but the
        // stopgap is offered: the tails rigged as ONE chain through the
        // bundle's centre, moving as one bushy mass (the kitsune, 2026-09-25).
        const note = document.createElement('span');
        note.className = 'zone-flyout-note';
        note.setAttribute('role', 'alert');
        note.dataset['tailFan'] = 'refused';
        note.textContent = state.fanRefusal;
        // First take the whole fan (the walk stops at the fork, one tail in), then group it.
        if (state.tips === false) {
          chain.append(
            button('extend tips', "take the fan's other tails into this zone — beyond the seam only, never the hips", () => deps.onTips(), { reach: 'tips' }),
          );
        }
        const group = button(
          'Group as one tail',
          'rig the tails as ONE chain through the middle of the bundle — they move together as one bushy tail (separate chains come later)',
          (e) => deps.onTail('group', e.isTrusted),
          { tail: 'group' },
        );
        group.className = 'flyout-digit zone-flyout-primary';
        chain.append(note, group);
        if (state.centreOffer !== null) {
          const o = state.centreOffer;
          chain.append(
            button(
              'Centre + group',
              `the bundle is swept ${o.degrees.toFixed(0)}° to the ${o.side} — turn it to centre-back about its root, then group it as one tail`,
              (e) => deps.onTail('group-centred', e.isTrusted),
              { tail: 'group-centred' },
            ),
          );
        }
      } else if (state.tail === null) {
        const grow = button(
          'Grow tail bones',
          'place a chain of tail bones down the middle of this flesh — the count starts from its length',
          (e) => deps.onTail('grow', e.isTrusted),
          { tail: 'grow' },
        );
        grow.className = 'flyout-digit zone-flyout-primary';
        chain.append(grow);
        if (state.centreOffer !== null) {
          const o = state.centreOffer;
          chain.append(
            button(
              'Centre + grow',
              `this tail is swept ${o.degrees.toFixed(0)}° to the ${o.side} — turn it to centre-back about its root, then grow its bones`,
              (e) => deps.onTail('grow-centred', e.isTrusted),
              { tail: 'grow-centred' },
            ),
          );
        }
      } else {
        const t = state.tail;
        const count = document.createElement('span');
        count.className = 'flyout-knuckle-label';
        count.dataset['tailCount'] = String(t.bones);
        count.textContent = `${t.bones} bones${t.lengthCm !== null ? ` · ${t.lengthCm.toFixed(0)} cm` : ''}`;
        count.title =
          (t.provenance === 'default' ? 'counted from its length' : t.defaultCount !== null ? `its length says ${t.defaultCount}` : 'your count') +
          ' · more bones = a finer curve, not a stiffer tail';
        chain.append(
          count,
          button('fewer bones', 'fewer bones: a coarser curve', (e) => deps.onTail('fewer', e.isTrusted), { tail: 'fewer' }),
          button('more bones', 'more bones: a finer curve (not a stiffer tail)', (e) => deps.onTail('more', e.isTrusted), { tail: 'more' }),
        );
        if (state.centreOffer !== null) {
          const o = state.centreOffer;
          chain.append(
            button(
              'centre tail',
              `swept ${o.degrees.toFixed(0)}° to the ${o.side} — turn it to centre-back about its root (undo puts it back)`,
              (e) => deps.onTail('centre', e.isTrusted),
              { tail: 'centre' },
            ),
          );
        }
      }
      if (state.tailNote !== null) {
        const note = document.createElement('span');
        note.className = 'zone-flyout-note';
        note.setAttribute('role', 'alert');
        note.textContent = state.tailNote;
        chain.append(note);
      }
    }
    // Soft tissue points at where its leaf is grown — the Body tab's region card.
    if (state.intent === 'softTissue') {
      const note = document.createElement('span');
      note.className = 'zone-flyout-note zone-flyout-plain';
      note.textContent = 'Soft tissue — pick this zone on a region card (Body → Soft tissue) to grow its bounce leaf over it.';
      row('chain').append(note);
    }
    // Cloth says plainly what it is not yet — one line, no buttons pretending.
    if (state.intent === 'cloth') {
      const note = document.createElement('span');
      note.className = 'zone-flyout-note zone-flyout-plain';
      note.textContent = 'Cloth bones are not available yet — this flesh is recorded as cloth.';
      row('chain').append(note);
    }

    // Row 3 — DONE is not REMOVE (Allen's read #3: the ✕ that removed the zone
    // was the only exit, "which of course then de-identified my tail").
    const actions = row('actions');
    const done = button('done', 'keep this zone as it is and close the bar (Esc)', () => deps.onDismiss(), { action: 'done' });
    done.className = 'flyout-digit';
    const more = button(showMore ? 'fewer options' : 'options…', 'wider / tighter, remove the chain, remove the zone', () => {
      showMore = !showMore;
      render();
    }, { action: 'more' });
    more.setAttribute('aria-expanded', String(showMore));
    actions.append(done, more);
    // The WAND (ADR-031 §3.2 T3; Allen, 2026-09-25): arm ➕ or ➖, then click the
    // model — ➕ grows the patch round the click toward this zone, ➖ gives body
    // flesh back to the body. One click, one undoable edit. On THIS row: the bar
    // stays three short rows (Allen's second read; the tail e2e pins it).
    for (const [mode, label, tip] of [
      ['add', '➕', 'wand: add — then click the model; the faces round the click join this zone, pulled toward it (never flesh on the bones unless you click there)'],
      ['remove', '➖', 'wand: remove — then click the model; the faces round the click leave this zone, pulled toward the body (never tail flesh unless you click there)'],
    ] as const) {
      const b = button(label, tip, (e) => deps.onWand(mode, e.isTrusted), { wand: mode });
      b.className = 'flyout-digit';
      b.setAttribute('aria-pressed', String(state.wand === mode));
      b.setAttribute('aria-label', mode === 'add' ? 'wand: add faces' : 'wand: remove faces');
      actions.append(b);
    }
    if (showMore) {
      const extras = row('more');
      const r = state.rings;
      const reach =
        r === null
          ? ''
          : r < 0
            ? ` (${-r} ring${r === -1 ? '' : 's'} shed at the body now)`
            : ` (${r} ring${r === 1 ? '' : 's'} toward the body now)`;
      extras.append(
        state.reachKind === 'tail'
          ? button('extend stem', `grow one ring of faces toward the body only — never sideways into the tail's neighbours${reach}`, () => deps.onReach(1), { reach: 'wider' })
          : button('wider', `grow one ring of faces toward the body${reach}`, () => deps.onReach(1), { reach: 'wider' }),
        button('tighter', `shed one ring of faces at the body${reach}`, () => deps.onReach(-1), { reach: 'tighter' }),
      );
      if (state.tips !== null) {
        extras.append(
          state.tips
            ? button('tips ✓', "the fan's other tails are in this zone — press to give them back", () => deps.onTips(), { reach: 'tips' })
            : button('extend tips', "take the fan's other tails into this zone — beyond the seam only, never the hips", () => deps.onTips(), { reach: 'tips' }),
        );
      }
      if (state.tail !== null) {
        if (state.tail.pivotPlaced) {
          extras.append(
            button('root: reset', 'put the tail’s pivot back where it was measured (drag the round root handle to place it)', (e) => deps.onTail('reset-root', e.isTrusted), {
              tail: 'reset-root',
            }),
          );
        }
        extras.append(
          button('remove chain', 'withdraw the chain; the zone stays (undo brings it back)', (e) => deps.onTail('withdraw', e.isTrusted), {
            tail: 'withdraw',
          }),
        );
      }
      const del = button('remove zone', 'not a zone — remove it (undo brings it back)', () => deps.onDelete(), { action: 'delete' });
      del.setAttribute('aria-label', 'remove this zone');
      extras.append(del);
    }
  }

  const onKey = (event: KeyboardEvent): void => {
    if (!open || event.key !== 'Escape') return;
    event.stopPropagation();
    deps.onDismiss();
  };
  // Dismiss on an outside CLICK, never on an outside DRAG: an orbit begins with
  // a pointerdown on the canvas, and the first cut closed the bar on every
  // orbit (Allen, read #2: "if I click outside of the flag — e.g. to rotate —
  // the flag disappears"). The pin tool's own rule: travel beyond a few pixels
  // between press and release is an orbit, not a pick.
  const CLICK_TRAVEL_PX = 5;
  let outsidePress: { x: number; y: number } | null = null;
  /**
   * Part of the bar's interaction though not inside it: an on-model handle the bar opened (the
   * tail's root handle, `data-flyout-companion`). A click or a fine drag on it is no outside click
   * (Codex P2, #253: a ≤5 px drag closed the bar and cancelled the placement).
   */
  const insideBar = (target: EventTarget | null): boolean =>
    root.contains(target as Node) || (target instanceof Element && target.closest('[data-flyout-companion]') !== null);
  const onOutsideDown = (event: PointerEvent): void => {
    if (!open || insideBar(event.target)) return;
    outsidePress = { x: event.clientX, y: event.clientY };
  };
  const onOutsideUp = (event: PointerEvent): void => {
    const press = outsidePress;
    outsidePress = null;
    if (!open || press === null || insideBar(event.target)) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > CLICK_TRAVEL_PX) return; // an orbit
    // An ARMED WAND owns clicks on the model (Allen, Damien, 2026-09-25: "the flag
    // appeared to close … when I tried to add some extra faces" — this listener
    // runs first, in capture, and closing disarmed the wand before its click).
    if (state.wand !== null) return;
    deps.onDismiss();
  };

  function close(): void {
    if (!open) return;
    open = false;
    cancelAnimationFrame(frame);
    root.hidden = true;
    outsidePress = null;
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('pointerdown', onOutsideDown, true);
    window.removeEventListener('pointerup', onOutsideUp, true);
    showMore = false;
    deps.onClosed();
  }

  return {
    open(next: ZoneFlyoutState): void {
      state = next;
      render();
      if (!open) {
        open = true;
        root.hidden = false;
        window.addEventListener('keydown', onKey, true);
        // Deferred so the click that opened the bar is not the click that closes it.
        setTimeout(() => {
          if (!open) return;
          window.addEventListener('pointerdown', onOutsideDown, true);
          window.addEventListener('pointerup', onOutsideUp, true);
        }, 0);
      }
      reposition();
    },
    update(next: ZoneFlyoutState): void {
      state = next;
      if (open) render();
    },
    close,
    get isOpen(): boolean {
      return open;
    },
    dispose(): void {
      close();
      root.remove();
    },
  };
}
